import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { CIF } from "@/constants/testIds";

const links = [
  { href: "#problem", label: "Problem", id: CIF.navLinkProblem },
  { href: "#how", label: "How it works", id: CIF.navLinkHow },
  { href: "#breakdown", label: "Cost breakdown", id: CIF.navLinkBreakdown },
  { href: "#why", label: "Why Estimated CIF", id: CIF.navLinkWhy },
  { href: "#workspace", label: "Workspace", id: CIF.navLinkWorkspace },
  { href: "#faq", label: "FAQ", id: CIF.navLinkFaq },
];

export const Nav = ({ onWorkspaceClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={CIF.nav}
      className={[
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-white/85 backdrop-blur-xl backdrop-saturate-150 border-b border-slate-200/70"
          : "bg-transparent border-b border-transparent",
      ].join(" ")}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" data-testid={CIF.navLogo} className="flex items-center gap-2.5 group">
          <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-900 text-white">
            <span className="absolute inset-0 rounded-md bg-gradient-to-br from-blue-500/30 via-transparent to-transparent" />
            <span className="font-display text-[13px] font-bold tracking-tighter relative">DM</span>
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="font-display text-[15px] font-medium tracking-tight text-slate-900">DeMaxtore</span>
            <span className="hidden sm:inline-block text-[10px] tracking-[0.22em] uppercase text-slate-400 font-semibold">Workspace</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="nav-link text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="#hero-calc"
            className="hidden md:inline-flex items-center text-[13px] font-medium text-slate-700 hover:text-slate-900 px-3 py-2"
          >
            Sign in
          </a>
          <Button
            data-testid={CIF.navCtaWorkspace}
            onClick={onWorkspaceClick}
            className="btn-shine h-9 rounded-full px-4 bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium"
          >
            Create Workspace <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </Button>
          <button
            className="lg:hidden ml-1 p-2 text-slate-700"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`${l.id}-mobile`}
                onClick={() => setOpen(false)}
                className="py-2.5 text-[14px] text-slate-700 border-b border-slate-100 last:border-0"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
